import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import ReportView from "../ReportPubblico/ReportView";
import { API_URL, authHeaders, nf, formatData, AvvisoEsecuzioni, BadgeStatoReport, useCopia } from "./comuni";

/**
 * Editor di un report di quartiere.
 *
 * BOZZA: si sceglie cosa pubblicare. La config (tratte, foto per tratta, KPI, note,
 * titolo, periodo) si salva da sola poco dopo ogni modifica (PUT), cosi' chiudere la
 * pagina non perde niente. "Anteprima" mostra ESATTAMENTE la pagina pubblica (stesso
 * componente ReportView, dati da /anteprima). "Pubblica" congela lo snapshot e crea il
 * link.
 *
 * PUBBLICATO / DISATTIVATO: niente piu' modifiche. Si vede il link con copia/apri, si
 * disattiva o riattiva, si rigenera il token, si duplica in una nuova bozza. L'anteprima
 * mostra lo snapshot salvato, che e' quello che vede chi apre il link. Unica modifica
 * ammessa: ritirare singole foto (sezione "Foto nel report"), stesso link; il file resta
 * nell'archivio GCS.
 *
 * Le foto: quando si include una tratta, le sue foto disponibili entrano tutte; si
 * tolgono una per una dal riquadro della tratta. Le tratte senza geometria si possono
 * georeferenziare da qui ("Georeferenzia"): il backend cerca la via su OpenStreetMap.
 */

const BASE = `${API_URL}/gestione-percorsi/report-quartiere`;
const fotoImgUrl = (key, w) => `${API_URL}/gestione-percorsi/percorsi/foto-img?key=${encodeURIComponent(key)}${w ? `&w=${w}` : ""}`;

const dataOraFoto = (f) => (f.scattataIl
    ? new Date(f.scattataIl).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : formatData(f.data));

const ETICHETTA_STATO = { effettuata: "Effettuata", non_effettuata: "Non effettuata", da_verificare: "Da verificare" };
const BADGE_STATO = { effettuata: "badge-success", non_effettuata: "badge-danger", da_verificare: "badge-neutral" };
const ORDINE_STATO = { non_effettuata: 0, da_verificare: 1, effettuata: 2 };

/** Da config (array) a Map codice -> Set(fotoKey), e ritorno. */
const configToSel = (cfg) => new Map((cfg?.tratte || []).map((t) => [t.codice, new Set(t.foto || [])]));
const selToConfig = (sel, kpi, note) => ({
    kpi: [...kpi],
    tratte: [...sel.entries()].map(([codice, foto]) => ({ codice, foto: [...foto] })),
    note: note || null,
});

const EditorReportQuartiere = ({ id, catalogo, onChiudi, onDuplica }) => {
    const [report, setReport] = useState(null);
    const [dati, setDati] = useState(null);
    const [loadingDati, setLoadingDati] = useState(false);
    const [errore, setErrore] = useState(null);

    // Scelte dell'utente (solo in bozza)
    const [sel, setSel] = useState(new Map());
    const [kpi, setKpi] = useState(new Set());
    const [note, setNote] = useState("");
    const [titolo, setTitolo] = useState("");
    const [periodo, setPeriodo] = useState({ dal: "", al: "" });

    const [salvataggio, setSalvataggio] = useState("ok"); // ok | in-corso | errore | sporco
    const [anteprima, setAnteprima] = useState(null);     // snapshot per il modale
    const [caricandoAnteprima, setCaricandoAnteprima] = useState(false);
    const [pubblicando, setPubblicando] = useState(false);
    const [confermaPubblica, setConfermaPubblica] = useState(false);
    const [azione, setAzione] = useState(null);
    const [geocodifica, setGeocodifica] = useState(null); // stato del lavoro di georeferenziazione
    const [bloccoMappa, setBloccoMappa] = useState(null); // risposta 409 "senza-mappa"

    // Filtri elenco tratte
    const [ricerca, setRicerca] = useState("");
    const [filtroStato, setFiltroStato] = useState("tutte");
    const [fotoAperte, setFotoAperte] = useState(new Set()); // codici con il riquadro foto aperto

    // Report pubblicato: lo snapshot salvato (con i fotoKeys, per le miniature) e la foto in rimozione
    const [pubblicato, setPubblicato] = useState(null);
    const [rimuovendo, setRimuovendo] = useState(null);
    const [fotoDaRimuovere, setFotoDaRimuovere] = useState(null); // { foto, tratta } in attesa di conferma

    /* Foto ingrandita mentre si sceglie: si tiene il CODICE della tratta e non le sue foto,
       cosi' la spunta (bozza) e l'elenco dopo una rimozione (pubblicato) restano aggiornati. */
    const [zoom, setZoom] = useState(null); // { codice, indice }

    const { copia, copiato } = useCopia();
    const bozza = report?.stato === "bozza";

    /* Stabile per lo stesso snapshot, e dichiarata qui perche' piu' in basso ci sono
       return condizionali: la mappa dell'anteprima la usa come dipendenza. */
    const fotoUrlAnteprima = useCallback((fotoId, size) => {
        const key = anteprima?.fotoKeys?.[fotoId];
        return key ? fotoImgUrl(key, size === "full" ? 1600 : 480) : "";
    }, [anteprima]);

    /* ------------------------------------------------ caricamento --- */

    const caricaReport = useCallback(async () => {
        const r = await axios.get(`${BASE}/${id}`, { headers: authHeaders() });
        const rep = r.data;
        setReport(rep);
        setSel(configToSel(rep.config));
        setKpi(new Set(rep.config?.kpi || catalogo.kpiDefault || []));
        setNote(rep.config?.note || "");
        setTitolo(rep.titolo || "");
        setPeriodo({ dal: rep.dal, al: rep.al });
        return rep;
    }, [id, catalogo.kpiDefault]);

    // `zona` = quella del report (i report di prima del Ponente arrivano gia' come "levante").
    const caricaDati = useCallback(async (quartiereId, dal, al, zona) => {
        setLoadingDati(true);
        setErrore(null);
        try {
            const r = await axios.get(`${BASE}/dati`, { params: { quartiereId, dal, al, zona }, headers: authHeaders() });
            setDati(r.data);
        } catch (e) {
            setErrore(e?.response?.data?.error || "Errore nel caricamento delle tratte del periodo");
        } finally {
            setLoadingDati(false);
        }
    }, []);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const rep = await caricaReport();
                if (vivo && rep.stato === "bozza") await caricaDati(rep.quartiereId, rep.dal, rep.al, rep.zona);
            } catch (e) {
                if (vivo) setErrore(e?.response?.data?.error || "Errore nel caricamento del report");
            }
        })();
        return () => { vivo = false; };
    }, [caricaReport, caricaDati]);

    /* Report pubblicato: le foto dello snapshot, per poterle ritirare dal link. Si leggono
       da /anteprima perche' e' l'unica risposta (dietro login) che porta i fotoKeys: il
       dettaglio /:id li toglie come il link pubblico. */
    const caricaPubblicato = useCallback(async () => {
        try {
            const r = await axios.get(`${BASE}/${id}/anteprima`, { headers: authHeaders() });
            setPubblicato(r.data);
        } catch (e) {
            setErrore(e?.response?.data?.error || "Errore nel caricamento delle foto del report");
        }
    }, [id]);

    useEffect(() => {
        if (report && !bozza) caricaPubblicato();
    }, [report?.stato, bozza, caricaPubblicato]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Toglie dal report pubblicato la foto confermata nel modale (solo dal report: il file su GCS resta). */
    const confermaRimozioneFoto = async () => {
        const f = fotoDaRimuovere?.foto;
        if (!f) return;
        setRimuovendo(f.id);
        try {
            const r = await axios.post(`${BASE}/${id}/foto/${f.id}/rimuovi`, {}, { headers: authHeaders() });
            setReport(r.data);
            await caricaPubblicato();
            setFotoDaRimuovere(null);
        } catch (e) {
            alert(e?.response?.data?.error || "Rimozione non riuscita");
        } finally {
            setRimuovendo(null);
        }
    };

    /* ------------------------------------------------- autosave --- */

    const primoRender = useRef(true);
    const timerRef = useRef(null);
    useEffect(() => {
        if (!report || !bozza) return undefined;
        if (primoRender.current) { primoRender.current = false; return undefined; }
        setSalvataggio("sporco");
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setSalvataggio("in-corso");
            try {
                const r = await axios.put(`${BASE}/${id}`, {
                    titolo, dal: periodo.dal, al: periodo.al, config: selToConfig(sel, kpi, note),
                }, { headers: authHeaders() });
                setReport((prev) => ({ ...prev, ...r.data }));
                setSalvataggio("ok");
            } catch (e) {
                setSalvataggio("errore");
            }
        }, 700);
        return () => clearTimeout(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sel, kpi, note, titolo, periodo]);

    /* Il periodo cambia -> le tratte del periodo vanno ricaricate (le scelte su tratte che
       spariscono dal nuovo periodo restano in config ma cadono alla pubblicazione). */
    const periodoCaricato = useRef(null);
    useEffect(() => {
        if (!report || !bozza || !periodo.dal || !periodo.al || periodo.al < periodo.dal) return;
        const chiave = `${periodo.dal}|${periodo.al}`;
        if (periodoCaricato.current === null) { periodoCaricato.current = chiave; return; }
        if (periodoCaricato.current === chiave) return;
        periodoCaricato.current = chiave;
        caricaDati(report.quartiereId, periodo.dal, periodo.al, report.zona);
    }, [periodo, report, bozza, caricaDati]);

    /* ------------------------------------------------- derivati --- */

    const tratte = useMemo(() => dati?.tratte || [], [dati]);
    const conteggi = useMemo(() => {
        const c = { effettuata: 0, non_effettuata: 0, da_verificare: 0, senzaGeo: 0, viaIntera: 0, daControllare: 0, foto: 0 };
        for (const t of tratte) {
            c[t.stato] += 1;
            if (!t.geometria) c.senzaGeo += 1;
            else if (t.geometriaQualita === "via-intera") c.viaIntera += 1;
            else if (t.geometriaAttendibile === false) c.daControllare += 1;
            c.foto += t.foto.length;
        }
        return c;
    }, [tratte]);
    const filtrate = useMemo(() => {
        const q = ricerca.trim().toLowerCase();
        return tratte
            .filter((t) => filtroStato === "tutte" || (filtroStato === "selezionate" ? sel.has(t.codice) : t.stato === filtroStato))
            .filter((t) => !q || `${t.strada} ${t.tratto} ${t.codice}`.toLowerCase().includes(q))
            .sort((a, b) => (ORDINE_STATO[a.stato] ?? 9) - (ORDINE_STATO[b.stato] ?? 9)
                || a.strada.localeCompare(b.strada, "it") || a.tratto.localeCompare(b.tratto, "it"));
    }, [tratte, ricerca, filtroStato, sel]);
    const fotoSelezionate = useMemo(() => [...sel.values()].reduce((n, s) => n + s.size, 0), [sel]);
    const codiciSenzaGeo = useMemo(() => tratte.filter((t) => !t.geometria).map((t) => t.codice), [tratte]);
    /* Tratte SCELTE che non si possono disegnare: sono queste a bloccare la pubblicazione
       (una tratta senza geometria che non e' nel report non fa danno). */
    const selSenzaGeo = useMemo(
        () => tratte.filter((t) => sel.has(t.codice) && !t.geometria).map((t) => t.codice),
        [tratte, sel]
    );
    /* Cosa impedisce di pubblicare adesso. Il caricamento, la selezione vuota e la
       georeferenziazione in corso spengono il pulsante: non c'e' niente da decidere, si
       aspetta. Le tratte senza mappa invece lo lasciano acceso, perche' la decisione e'
       dell'utente (toglierle o pubblicare lo stesso) e si prende nel modale. */
    const motivoBlocco = !bozza ? null
        : loadingDati ? "Caricamento delle tratte in corso"
            : sel.size === 0 ? "Seleziona almeno una tratta"
                : geocodifica?.inCorso ? "Georeferenziazione in corso: attendi che finisca"
                    : null;
    const pubblicabile = !motivoBlocco;
    const avvisoSenzaMappa = !motivoBlocco && selSenzaGeo.length
        ? `${nf(selSenzaGeo.length)} tratte selezionate non sono sulla mappa`
        : null;

    /** Il pulsante Pubblica: prima il nodo delle tratte non disegnabili, poi la conferma. */
    const chiediPubblicazione = () => {
        if (selSenzaGeo.length) {
            const senza = tratte.filter((t) => sel.has(t.codice) && !t.geometria);
            setBloccoMappa({
                totaleSenzaMappa: senza.length,
                senzaMappa: senza.slice(0, 50).map((t) => ({ codice: t.codice, strada: t.strada, tratto: t.tratto })),
            });
            return;
        }
        setConfermaPubblica(true);
    };

    /* ------------------------------------------------- azioni --- */

    const toggleTratta = (t) => setSel((prev) => {
        const next = new Map(prev);
        if (next.has(t.codice)) next.delete(t.codice);
        else next.set(t.codice, new Set(t.foto.map((f) => f.key))); // tutte le foto, si tolgono dopo
        return next;
    });
    const toggleFoto = (codice, key) => setSel((prev) => {
        if (!prev.has(codice)) return prev;
        const next = new Map(prev);
        const s = new Set(next.get(codice));
        if (s.has(key)) s.delete(key); else s.add(key);
        next.set(codice, s);
        return next;
    });
    const selezionaTutte = (filtro) => setSel((prev) => {
        const next = new Map(prev);
        for (const t of tratte) if (!filtro || filtro(t)) next.set(t.codice, next.get(t.codice) || new Set(t.foto.map((f) => f.key)));
        return next;
    });
    const deselezionaTutte = () => setSel(new Map());
    const toggleKpi = (kid) => setKpi((prev) => { const n = new Set(prev); if (n.has(kid)) n.delete(kid); else n.add(kid); return n; });
    const toggleFotoAperte = (codice) => setFotoAperte((prev) => { const n = new Set(prev); if (n.has(codice)) n.delete(codice); else n.add(codice); return n; });

    /** Salva subito (senza aspettare il debounce) e poi esegue `fn`. */
    const salvaOra = async () => {
        if (!bozza) return;
        clearTimeout(timerRef.current);
        setSalvataggio("in-corso");
        const r = await axios.put(`${BASE}/${id}`, { titolo, dal: periodo.dal, al: periodo.al, config: selToConfig(sel, kpi, note) }, { headers: authHeaders() });
        setReport((prev) => ({ ...prev, ...r.data }));
        setSalvataggio("ok");
    };

    const apriAnteprima = async () => {
        setCaricandoAnteprima(true);
        try {
            await salvaOra();
            const r = await axios.get(`${BASE}/${id}/anteprima`, { headers: authHeaders() });
            setAnteprima(r.data);
        } catch (e) {
            alert(e?.response?.data?.error || "Anteprima non disponibile");
        } finally {
            setCaricandoAnteprima(false);
        }
    };

    const pubblica = async ({ consentiSenzaMappa = false } = {}) => {
        setPubblicando(true);
        try {
            await salvaOra();
            const r = await axios.post(`${BASE}/${id}/pubblica`, { consentiSenzaMappa }, { headers: authHeaders() });
            setReport(r.data);
            setConfermaPubblica(false);
            setBloccoMappa(null);
        } catch (e) {
            // Il backend rifiuta se restano tratte non disegnabili: lo si mostra con le
            // vie coinvolte e la scelta fra togliere quelle tratte o procedere lo stesso.
            if (e?.response?.data?.codice === "senza-mappa") setBloccoMappa(e.response.data);
            else alert(e?.response?.data?.error || "Pubblicazione non riuscita");
        } finally {
            setPubblicando(false);
        }
    };

    /** Toglie dalla selezione le tratte che non si possono disegnare. */
    const escludiSenzaMappa = () => {
        const senza = new Set(tratte.filter((t) => !t.geometria).map((t) => t.codice));
        setSel((prev) => {
            const next = new Map(prev);
            for (const c of senza) next.delete(c);
            return next;
        });
        setBloccoMappa(null);
        setConfermaPubblica(true);   // tolte quelle, si va dritti alla conferma
    };

    const esegui = async (verbo) => {
        setAzione(verbo);
        try {
            const r = await axios.post(`${BASE}/${id}/${verbo}`, {}, { headers: authHeaders() });
            if (verbo === "duplica") onDuplica(r.data.id);
            else setReport(r.data);
        } catch (e) {
            alert(e?.response?.data?.error || "Operazione non riuscita");
        } finally {
            setAzione(null);
        }
    };

    /* La georeferenziazione gira nel backend e puo' durare minuti: qui si avvia, si segue
       l'avanzamento con un polling, e quando finisce si ricaricano le tratte (cosi' la
       colonna Mappa si aggiorna da sola). Finche' e' in corso, "Pubblica" resta spento:
       un report con tratte non disegnabili non si manda al cliente. */
    const georeferenzia = async () => {
        if (!codiciSenzaGeo.length || geocodifica?.inCorso) return;
        setGeocodifica({ inCorso: true, messaggio: "avvio…" });
        try {
            const r = await axios.post(`${BASE}/geometrie/geocodifica`, { codici: codiciSenzaGeo.slice(0, 300) }, { headers: authHeaders() });
            setGeocodifica(r.data);
        } catch (e) {
            if (e?.response?.status === 409) setGeocodifica(e.response.data.stato || { inCorso: true });
            else setGeocodifica({ inCorso: false, errore: e?.response?.data?.error || "Georeferenziazione non riuscita" });
        }
    };

    // Polling dello stato finche' il lavoro e' in corso; a fine corsa ricarica le tratte.
    useEffect(() => {
        if (!geocodifica?.inCorso) return undefined;
        let vivo = true;
        const id = setInterval(async () => {
            try {
                const r = await axios.get(`${BASE}/geometrie/stato`, { headers: authHeaders() });
                if (!vivo) return;
                setGeocodifica(r.data);
                if (!r.data.inCorso) {
                    clearInterval(id);
                    if (report) await caricaDati(report.quartiereId, periodo.dal, periodo.al, report.zona);
                }
            } catch (_) { /* un tentativo perso non interrompe il polling */ }
        }, 3000);
        return () => { vivo = false; clearInterval(id); };
    }, [geocodifica?.inCorso, report, periodo.dal, periodo.al, caricaDati]);

    // All'apertura: se un lavoro era gia' in corso (avviato altrove), riaggancialo.
    useEffect(() => {
        if (!bozza) return;
        axios.get(`${BASE}/geometrie/stato`, { headers: authHeaders() })
            .then((r) => { if (r.data?.inCorso) setGeocodifica(r.data); })
            .catch(() => {});
    }, [bozza]);

    /* ------------------------------------------------- render --- */

    if (!report) return errore ? <div className="gp-rep-avviso"><span>{errore}</span></div> : <Lf23Loader testo="Caricamento report…" />;

    const testoSalvataggio = { ok: "Salvato", "in-corso": "Salvataggio…", sporco: "Modifiche non salvate", errore: "Salvataggio non riuscito" }[salvataggio];

    // Foto ingrandita: in bozza le tratte del periodo, da pubblicato quelle dello snapshot
    const trattaZoom = zoom ? (bozza ? tratte : pubblicato?.tratte || []).find((t) => t.codice === zoom.codice) : null;
    const fotoZoom = trattaZoom?.foto || [];
    const indiceZoom = zoom ? Math.min(zoom.indice, fotoZoom.length - 1) : 0;

    return (
        <div className="gp-rq gp-rq-editor">
            {/* Barra in cima: indietro, titolo, stato, azioni principali */}
            <div className="gp-rq-testa">
                <button className="btn btn-ghost gp-new-btn" onClick={onChiudi}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
                    Elenco report
                </button>
                <div className="gp-rq-titolo">
                    {bozza ? (
                        <input className="gp-rq-titolo-input" value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo del report" maxLength={255} aria-label="Titolo del report" />
                    ) : (
                        <div className="gp-rq-titolo-testo">{report.titolo}</div>
                    )}
                    <div className="sub">
                        Quartiere <strong>{report.quartiere}</strong> · {formatData(report.dal)} – {formatData(report.al)}
                    </div>
                </div>
                <BadgeStatoReport stato={report.stato} />
                {bozza && <span className={`gp-rq-salvataggio ${salvataggio}`}>{testoSalvataggio}</span>}
                <button className="btn btn-ghost gp-new-btn" onClick={apriAnteprima} disabled={caricandoAnteprima}>
                    {caricandoAnteprima ? "Apertura…" : "Anteprima"}
                </button>
                {bozza && (
                    <button className="btn btn-success gp-new-btn" onClick={chiediPubblicazione}
                        disabled={!pubblicabile} title={motivoBlocco || avvisoSenzaMappa || ""}>
                        Pubblica
                    </button>
                )}
            </div>

            {errore && <div className="gp-rep-avviso"><span>{errore}</span></div>}

            {/* Report pubblicato o disattivato: pannello del link */}
            {!bozza && (
                <div className="gp-rq-card gp-rq-link-card">
                    <div className="gp-rq-card-head">
                        <h3>Link pubblico</h3>
                        <span className="sub">{report.attivo ? "Chiunque abbia il link può aprire il report, senza login." : "Il link è disattivato: chi lo apre vede “Report non disponibile”."}</span>
                    </div>
                    <div className="gp-rq-url">
                        <code className={report.attivo ? "" : "spento"}>{report.url}</code>
                        <button className="btn btn-primary gp-new-btn" onClick={() => copia(report.url, "url")}>{copiato === "url" ? "Copiato!" : "Copia link"}</button>
                        <a className="btn btn-ghost gp-new-btn" href={report.url} target="_blank" rel="noreferrer">Apri</a>
                    </div>
                    <div className="gp-rq-link-azioni">
                        {report.attivo
                            ? <button className="btn btn-ghost gp-new-btn" disabled={!!azione} onClick={() => esegui("disattiva")}>Disattiva link</button>
                            : <button className="btn btn-success gp-new-btn" disabled={!!azione} onClick={() => esegui("riattiva")}>Riattiva link</button>}
                        <button className="btn btn-ghost gp-new-btn" disabled={!!azione} onClick={() => { if (window.confirm("Il link attuale smetterà di funzionare e ne verrà creato uno nuovo. Continuare?")) esegui("rigenera-token"); }}>Rigenera link</button>
                        <button className="btn btn-ghost gp-new-btn" disabled={!!azione} onClick={() => esegui("duplica")} title="Crea una nuova bozza con le stesse scelte, da modificare">Duplica in bozza</button>
                    </div>
                    <div className="gp-rq-riepilogo">
                        <span><strong>{nf(report.riepilogo?.tratte)}</strong> tratte</span>
                        <span><strong>{nf(report.riepilogo?.foto)}</strong> foto</span>
                        <span><strong>{nf(report.riepilogo?.kpi)}</strong> indicatori</span>
                        {report.riepilogo?.percentuale != null && <span><strong>{nf(report.riepilogo.percentuale, 1)}%</strong> completamento</span>}
                        {report.pubblicatoIl && <span>pubblicato il <strong>{new Date(report.pubblicatoIl).toLocaleString("it-IT")}</strong></span>}
                    </div>
                    <p className="sub">Il contenuto è uno snapshot: non cambia più, anche se i dati di Interacta o dell’app operatori cambiano. Per aggiornarlo, duplica il report e pubblica la nuova versione. Unica eccezione: le foto si possono ritirare qui sotto senza cambiare il link.</p>
                </div>
            )}

            {/* Report pubblicato: foto ritirabili una per una, stesso link */}
            {!bozza && pubblicato && (
                <FotoPubblicate tratte={pubblicato.tratte || []} fotoKeys={pubblicato.fotoKeys || {}}
                    aperte={fotoAperte} onApri={toggleFotoAperte} rimuovendo={rimuovendo}
                    onRimuovi={(foto, tratta) => setFotoDaRimuovere({ foto, tratta })}
                    onZoom={(codice, indice) => setZoom({ codice, indice })} />
            )}

            {/* Bozza: composizione */}
            {bozza && (
                <>
                    <div className="gp-rq-card">
                        <div className="gp-rq-card-head">
                            <h3>1. Periodo</h3>
                            <span className="sub">Cambiando le date si ricaricano le tratte del periodo.</span>
                        </div>
                        <div className="filters-grid gp-rq-periodo">
                            <div className="filter-group">
                                <label>Dal</label>
                                <DatePickerModal value={periodo.dal} onChange={(e) => setPeriodo((p) => ({ ...p, dal: e.target.value }))} max={periodo.al || undefined} label="Dal" />
                            </div>
                            <div className="filter-group">
                                <label>Al</label>
                                <DatePickerModal value={periodo.al} onChange={(e) => setPeriodo((p) => ({ ...p, al: e.target.value }))} min={periodo.dal || undefined} label="Al" />
                            </div>
                        </div>
                    </div>

                    <div className="gp-rq-card">
                        <div className="gp-rq-card-head">
                            <h3>2. Tratte e foto</h3>
                            <span className="sub">Spunta le tratte da mostrare; per ognuna puoi escludere singole foto. Le foto non spuntate non finiscono nel link.</span>
                        </div>

                        {loadingDati ? <Lf23Loader testo="Caricamento tratte del periodo…" /> : (
                            <>
                                <AvvisoEsecuzioni meta={dati?.meta} testoDanno="Nel report tutte le tratte risulterebbero non effettuate: pubblicare adesso darebbe al cliente un dato falso." />

                                <div className="gp-rq-chips">
                                    <span className="gp-rq-chip">Previste <strong>{nf(tratte.length)}</strong></span>
                                    <span className="gp-rq-chip ok">Effettuate <strong>{nf(conteggi.effettuata)}</strong></span>
                                    <span className="gp-rq-chip ko">Non effettuate <strong>{nf(conteggi.non_effettuata)}</strong></span>
                                    <span className="gp-rq-chip">Da verificare <strong>{nf(conteggi.da_verificare)}</strong></span>
                                    <span className="gp-rq-chip sel">Selezionate <strong>{nf(sel.size)}</strong> · foto <strong>{nf(fotoSelezionate)}</strong>/{nf(conteggi.foto)}</span>
                                    {conteggi.daControllare > 0 && (
                                        <span className="gp-rq-chip warn" title="Il pezzo ritagliato ha una lunghezza distante da quella dichiarata in anagrafica: vale la pena guardarlo in anteprima">
                                            Ritagli da controllare <strong>{nf(conteggi.daControllare)}</strong>
                                        </span>
                                    )}
                                    {conteggi.viaIntera > 0 && (
                                        <span className="gp-rq-chip warn" title="Per queste tratte non e' stato possibile delimitare il pezzo esatto: sulla mappa comparira' l'intera via">
                                            Disegnate per intero <strong>{nf(conteggi.viaIntera)}</strong>
                                        </span>
                                    )}
                                    {conteggi.senzaGeo > 0 && (
                                        <span className={`gp-rq-chip ${selSenzaGeo.length ? "ko" : "warn"}`}>
                                            Senza mappa <strong>{nf(conteggi.senzaGeo)}</strong>
                                            {selSenzaGeo.length > 0 && <> (di cui <strong>{nf(selSenzaGeo.length)}</strong> selezionate)</>}
                                        </span>
                                    )}
                                </div>

                                <div className="toolbar gp-rq-toolbar">
                                    <div className="search-box">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                        <input value={ricerca} onChange={(e) => setRicerca(e.target.value)} placeholder="Cerca via, tratto o codice" />
                                    </div>
                                    <select className="gp-rq-select" value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)} aria-label="Filtra per stato">
                                        <option value="tutte">Tutti gli stati</option>
                                        <option value="effettuata">Effettuate</option>
                                        <option value="non_effettuata">Non effettuate</option>
                                        <option value="da_verificare">Da verificare</option>
                                        <option value="selezionate">Solo selezionate</option>
                                    </select>
                                    <span className="spacer" />
                                    <button className="btn btn-ghost gp-new-btn" onClick={() => selezionaTutte()}>Seleziona tutte</button>
                                    <button className="btn btn-ghost gp-new-btn" onClick={() => { deselezionaTutte(); selezionaTutte((t) => t.stato === "effettuata"); }}>Solo effettuate</button>
                                    <button className="btn btn-ghost gp-new-btn" onClick={deselezionaTutte}>Nessuna</button>
                                    {codiciSenzaGeo.length > 0 && (
                                        <button className="btn btn-ghost gp-new-btn" onClick={georeferenzia} disabled={geocodifica?.inCorso} title="Cerca su OpenStreetMap le vie delle tratte senza geometria (evidenzia la via intera)">
                                            {geocodifica?.inCorso ? "Ricerca su OpenStreetMap…" : `Georeferenzia ${nf(codiciSenzaGeo.length)} tratte`}
                                        </button>
                                    )}
                                </div>

                                {geocodifica?.inCorso && (
                                    <div className="gp-rq-lavoro">
                                        <span className="gp-rq-spinner" aria-hidden="true" />
                                        <div>
                                            <strong>Georeferenziazione in corso</strong> — {geocodifica.messaggio || "…"}
                                            <div className="sub">Cerco le vie su OpenStreetMap: può richiedere qualche minuto. Puoi continuare a lavorare, la pubblicazione si sblocca quando ho finito.</div>
                                        </div>
                                    </div>
                                )}
                                {geocodifica && !geocodifica.inCorso && geocodifica.esito && (
                                    <div className="gp-rq-nota">
                                        Georeferenziazione conclusa: <strong>{nf(geocodifica.esito.trovate)}</strong> tratte disegnabili
                                        {geocodifica.ritaglio && (
                                            <> · <strong>{nf(geocodifica.ritaglio.ritagliate)}</strong> ritagliate sul tratto esatto
                                                {geocodifica.ritaglio.viaIntera > 0 && <>, {nf(geocodifica.ritaglio.viaIntera)} disegnate per intero</>}</>
                                        )}
                                        {geocodifica.esito.nonTrovate + geocodifica.esito.ambigue + geocodifica.esito.errori > 0 && (
                                            <> · <strong>{nf(geocodifica.esito.nonTrovate + geocodifica.esito.ambigue + geocodifica.esito.errori)}</strong> vie non risolte</>
                                        )}.
                                        {geocodifica.esito.irrisolte?.length > 0 && (
                                            <> Restano fuori: {geocodifica.esito.irrisolte.slice(0, 6).map((v) => v.strada).join(", ")}
                                                {geocodifica.esito.irrisolte.length > 6 ? ` e altre ${geocodifica.esito.irrisolte.length - 6}` : ""}.</>
                                        )}
                                    </div>
                                )}
                                {geocodifica?.errore && <div className="gp-rep-avviso"><span>{geocodifica.errore}</span></div>}

                                {tratte.length === 0 ? (
                                    <div className="gp-rq-vuoto">
                                        <strong>Nessuna tratta di {report.quartiere} nei percorsi di questo periodo.</strong>
                                        <span>Allarga il periodo oppure verifica che i percorsi siano stati generati.</span>
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <div className="table-scroll">
                                            <table className="gp-rq-table gp-rq-tratte">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 36 }}></th>
                                                        <th>Via / tratto</th>
                                                        <th>Stato</th>
                                                        <th>Passaggi</th>
                                                        <th>Foto</th>
                                                        <th>Mappa</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtrate.length === 0 && <tr><td colSpan={6} className="muted">Nessuna tratta corrisponde al filtro.</td></tr>}
                                                    {filtrate.map((t) => {
                                                        const inclusa = sel.has(t.codice);
                                                        const fotoSel = sel.get(t.codice) || new Set();
                                                        const fatti = t.passaggi.filter((p) => p.esito === "fatta").length;
                                                        const aperte = fotoAperte.has(t.codice);
                                                        return (
                                                            <RigaTratta key={t.codice} t={t} inclusa={inclusa} fotoSel={fotoSel} fatti={fatti} aperte={aperte}
                                                                onToggle={() => toggleTratta(t)} onToggleFoto={(k) => toggleFoto(t.codice, k)} onApriFoto={() => toggleFotoAperte(t.codice)}
                                                                onZoom={(indice) => setZoom({ codice: t.codice, indice })} />
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="gp-rq-card">
                        <div className="gp-rq-card-head">
                            <h3>3. Indicatori</h3>
                            <span className="sub">Le card in cima alla pagina pubblica. I valori si calcolano sulle sole tratte selezionate.</span>
                        </div>
                        <div className="gp-rq-kpi-grid">
                            {catalogo.kpiDisponibili.map((k) => (
                                <label key={k.id} className={`gp-rq-kpi ${kpi.has(k.id) ? "on" : ""}`}>
                                    <input type="checkbox" checked={kpi.has(k.id)} onChange={() => toggleKpi(k.id)} />
                                    <span>
                                        <strong>{k.label}</strong>
                                        <small>{k.descrizione}</small>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="gp-rq-card">
                        <div className="gp-rq-card-head">
                            <h3>4. Nota per il destinatario <span className="sub">(facoltativa)</span></h3>
                        </div>
                        <textarea className="gp-rq-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3}
                            placeholder="Es. Il servizio del 15/09 è stato sospeso per allerta meteo; le tratte sono state recuperate il 17/09." />
                    </div>

                    <div className="gp-rq-piede">
                        <span className={`gp-rq-salvataggio ${salvataggio}`}>{testoSalvataggio}</span>
                        <span className="spacer" />
                        {(motivoBlocco || avvisoSenzaMappa) && <span className="gp-rq-blocco">{motivoBlocco || avvisoSenzaMappa}</span>}
                        <button className="btn btn-ghost" onClick={apriAnteprima} disabled={caricandoAnteprima}>{caricandoAnteprima ? "Apertura…" : "Anteprima"}</button>
                        <button className="btn btn-success" onClick={chiediPubblicazione} disabled={!pubblicabile} title={motivoBlocco || avvisoSenzaMappa || ""}>Pubblica e genera il link</button>
                    </div>
                </>
            )}

            {/* Anteprima: la pagina pubblica, identica, in un modale a tutto schermo */}
            {anteprima && (
                <div className="gp-rq-anteprima-overlay" role="dialog" aria-modal="true" aria-label="Anteprima del report">
                    <div className="gp-rq-anteprima-bar">
                        <span>{anteprima.anteprima ? "Anteprima della pagina pubblica" : "Pagina pubblica (snapshot pubblicato)"}</span>
                        <span className="spacer" />
                        {bozza && <button className="btn btn-success gp-new-btn" onClick={() => { setAnteprima(null); chiediPubblicazione(); }} disabled={!pubblicabile} title={motivoBlocco || avvisoSenzaMappa || ""}>Pubblica</button>}
                        <button className="btn btn-ghost gp-new-btn" onClick={() => setAnteprima(null)}>Chiudi</button>
                    </div>
                    <div className="gp-rq-anteprima-corpo">
                        <ReportView report={anteprima} fotoUrl={fotoUrlAnteprima} anteprima={!!anteprima.anteprima} />
                    </div>
                </div>
            )}

            {bloccoMappa && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setBloccoMappa(null); }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon" style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)" }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Ci sono tratte che non compaiono sulla mappa</h3></div>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text">
                                <strong>{nf(bloccoMappa.totaleSenzaMappa)}</strong> tratte selezionate non sono georeferenziate: nel report resterebbero
                                solo nell’elenco. Di solito è un nome che OpenStreetMap scrive in modo diverso.
                            </p>
                            <ul className="gp-rq-elenco-vie">
                                {bloccoMappa.senzaMappa.slice(0, 12).map((t) => (
                                    <li key={t.codice}><strong>{t.strada}</strong>{t.tratto ? ` · ${t.tratto}` : ""}</li>
                                ))}
                                {bloccoMappa.totaleSenzaMappa > 12 && <li className="muted">e altre {nf(bloccoMappa.totaleSenzaMappa - 12)}…</li>}
                            </ul>
                            <p className="sub">Puoi riprovare la georeferenziazione, toglierle dal report, oppure pubblicare comunque (compariranno solo in elenco).</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setBloccoMappa(null)}>Annulla</button>
                            <button className="btn btn-ghost" onClick={escludiSenzaMappa}>Togli dal report</button>
                            <button className="btn btn-danger" onClick={() => pubblica({ consentiSenzaMappa: true })} disabled={pubblicando}>Pubblica comunque</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Foto ingrandita: in bozza si include/esclude da qui, da pubblicato si ritira */}
            {trattaZoom && fotoZoom.length > 0 && (
                <AnteprimaFoto
                    foto={fotoZoom}
                    indice={indiceZoom}
                    onIndice={(indice) => setZoom((z) => z && { ...z, indice })}
                    onChiudi={() => setZoom(null)}
                    src={(f) => {
                        const key = bozza ? f.key : pubblicato?.fotoKeys?.[f.id];
                        return key ? fotoImgUrl(key, 1600) : null;
                    }}
                    didascalia={(f) => {
                        const dove = `${trattaZoom.strada}${trattaZoom.tratto ? ` · ${trattaZoom.tratto}` : ""}`;
                        const stato = bozza ? (sel.get(trattaZoom.codice)?.has(f.key) ? " · nel report" : " · esclusa") : "";
                        return `${dove} — ${dataOraFoto(f)}${stato}`;
                    }}
                    azione={(f) => {
                        if (bozza) {
                            const on = !!sel.get(trattaZoom.codice)?.has(f.key);
                            return (
                                <button className={`btn ${on ? "btn-ghost" : "btn-success"}`} onClick={() => toggleFoto(trattaZoom.codice, f.key)}>
                                    {on ? "Escludi dal report" : "Includi nel report"}
                                </button>
                            );
                        }
                        return (
                            <button className="btn btn-danger" disabled={!!rimuovendo}
                                onClick={() => { setZoom(null); setFotoDaRimuovere({ foto: f, tratta: trattaZoom }); }}>
                                Rimuovi dal report
                            </button>
                        );
                    }}
                />
            )}

            {fotoDaRimuovere && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !rimuovendo) setFotoDaRimuovere(null); }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon" style={{ background: "linear-gradient(135deg, #EF4444, #DC2626)" }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Rimuovere la foto dal report?</h3></div>
                        </div>
                        <div className="modal-body">
                            <div className="gp-rq-rimuovi-anteprima">
                                {pubblicato?.fotoKeys?.[fotoDaRimuovere.foto.id]
                                    ? <Miniatura src={fotoImgUrl(pubblicato.fotoKeys[fotoDaRimuovere.foto.id], 480)} alt="Foto da rimuovere" />
                                    : <span className="gp-rq-foto-ko">non disponibile</span>}
                            </div>
                            <p className="modal-text">
                                <strong>{fotoDaRimuovere.tratta.strada}</strong>{fotoDaRimuovere.tratta.tratto ? ` · ${fotoDaRimuovere.tratta.tratto}` : ""}
                                {" — "}{fotoDaRimuovere.foto.scattataIl
                                    ? new Date(fotoDaRimuovere.foto.scattataIl).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                                    : formatData(fotoDaRimuovere.foto.data)}
                            </p>
                            <p className="modal-text">
                                La foto sparisce <strong>subito</strong> dal link pubblico, che resta lo stesso.
                                Non viene cancellata dall’archivio: se servisse di nuovo, duplica il report e rispuntala.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setFotoDaRimuovere(null)} disabled={!!rimuovendo}>Annulla</button>
                            <button className="btn btn-danger" onClick={confermaRimozioneFoto} disabled={!!rimuovendo}>{rimuovendo ? "Rimozione…" : "Rimuovi dal report"}</button>
                        </div>
                    </div>
                </div>
            )}

            {confermaPubblica && (
                <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !pubblicando) setConfermaPubblica(false); }}>
                    <div className="modal">
                        <div className="modal-header">
                            <div className="m-icon" style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                            </div>
                            <div style={{ flex: 1 }}><h3>Pubblicare il report?</h3></div>
                        </div>
                        <div className="modal-body">
                            <p className="modal-text">
                                Verrà creato un link pubblico, apribile <strong>senza login</strong>, con <strong>{nf(sel.size)} tratte</strong>, <strong>{nf(fotoSelezionate)} foto</strong> e <strong>{nf(kpi.size)} indicatori</strong>.
                                Il contenuto viene congelato adesso: per cambiarlo dovrai duplicare il report. Potrai disattivare il link in qualsiasi momento.
                            </p>
                            {dati?.meta?.sorgenteEsecuzione && dati.meta.sorgenteEsecuzione !== "ok" && (
                                <p className="modal-error">Attenzione: le esecuzioni non sono leggibili in questo ambiente, tutte le tratte risulteranno non effettuate.</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-ghost" onClick={() => setConfermaPubblica(false)} disabled={pubblicando}>Annulla</button>
                            <button className="btn btn-success" onClick={() => pubblica()} disabled={pubblicando}>{pubblicando ? "Pubblicazione…" : "Pubblica"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* Miniatura che non lascia l'icona rotta quando la foto non e' piu' sul bucket
   (scaduta o cancellata dall'app): si vede perche' non entrera' nel report. */
const Miniatura = ({ src, alt }) => {
    const [ko, setKo] = useState(false);
    if (ko) return <span className="gp-rq-foto-ko">non disponibile</span>;
    return <img src={src} alt={alt} loading="lazy" onError={() => setKo(true)} />;
};

/**
 * Foto ingrandita mentre si sceglie, con le altre foto della stessa tratta a portata di
 * frecce. Comandi come il lightbox della pagina pubblica (ReportView): frecce, Esc, click
 * fuori per chiudere. In piu' `azione(foto)`: il pulsante sulla foto corrente.
 * `src(foto)` restituisce null se la foto non ha piu' il file.
 */
const AnteprimaFoto = ({ foto, indice, onIndice, onChiudi, src, didascalia, azione }) => {
    const n = foto.length;
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onChiudi();
            if (e.key === "ArrowRight" && n > 1) onIndice((indice + 1) % n);
            if (e.key === "ArrowLeft" && n > 1) onIndice((indice - 1 + n) % n);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [n, indice, onIndice, onChiudi]);

    const f = foto[indice];
    const url = src(f);
    return (
        <div className="gp-lightbox gp-rq-lightbox" onClick={onChiudi} role="dialog" aria-modal="true" aria-label="Foto ingrandita">
            <button type="button" className="gp-lightbox-close" onClick={onChiudi} title="Chiudi" aria-label="Chiudi">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            {n > 1 && (
                <>
                    <button type="button" className="gp-rq-lightbox-nav prev" aria-label="Foto precedente"
                        onClick={(e) => { e.stopPropagation(); onIndice((indice - 1 + n) % n); }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                    </button>
                    <button type="button" className="gp-rq-lightbox-nav next" aria-label="Foto successiva"
                        onClick={(e) => { e.stopPropagation(); onIndice((indice + 1) % n); }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </button>
                </>
            )}
            <figure className="gp-rq-lightbox-figura" onClick={(e) => e.stopPropagation()}>
                {/* key: senza, lo stato "non disponibile" di una foto rotta resterebbe sulle successive */}
                {url ? <Miniatura key={url} src={url} alt={`foto ${indice + 1} di ${n}`} /> : <span className="gp-rq-foto-ko">non disponibile</span>}
                <figcaption>{didascalia(f)} — foto {indice + 1} di {n}</figcaption>
                {azione && <div className="gp-rq-lightbox-azioni">{azione(f)}</div>}
            </figure>
        </div>
    );
};

/**
 * Report pubblicato: le tratte con foto, ognuna col suo riquadro apribile. Ogni foto ha
 * "Rimuovi", che la toglie dallo snapshot (e quindi dal link) ma non dall'archivio.
 * Le miniature passano dal proxy interno col foto_key, come nell'editor in bozza.
 */
const FotoPubblicate = ({ tratte, fotoKeys, aperte, onApri, onRimuovi, onZoom, rimuovendo }) => {
    const conFoto = tratte.filter((t) => (t.foto || []).length > 0);
    const totale = conFoto.reduce((n, t) => n + t.foto.length, 0);
    return (
        <div className="gp-rq-card">
            <div className="gp-rq-card-head">
                <h3>Foto nel report</h3>
                <span className="sub">
                    {totale ? `${nf(totale)} foto in ${nf(conFoto.length)} tratte. ` : ""}
                    Una foto rimossa sparisce subito dal link pubblico; resta nell’archivio delle foto dei percorsi.
                </span>
            </div>
            {conFoto.length === 0 ? (
                <div className="gp-rq-vuoto"><span>Nessuna foto in questo report.</span></div>
            ) : (
                <div className="table-container">
                    <div className="table-scroll">
                        <table className="gp-rq-table gp-rq-tratte">
                            <thead>
                                <tr>
                                    <th>Via / tratto</th>
                                    <th>Foto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {conFoto.map((t) => {
                                    const aperta = aperte.has(t.codice);
                                    return (
                                        <React.Fragment key={t.codice}>
                                            <tr>
                                                <td onClick={() => onApri(t.codice)} className="gp-row-click">
                                                    <div className="strong">{t.strada}</div>
                                                    <div className="sub">{t.tratto || "—"} · <span className="codice">{t.codice}</span></div>
                                                </td>
                                                <td className="gp-rep-nowrap">
                                                    <button className="gp-rq-foto-btn" onClick={() => onApri(t.codice)} title="Mostra le foto">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                                                        {nf(t.foto.length)}
                                                        <span className={`chev ${aperta ? "open" : ""}`}>▾</span>
                                                    </button>
                                                </td>
                                            </tr>
                                            {aperta && (
                                                <tr className="gp-rq-foto-riga gp-rq-foto-riga-pub">
                                                    <td colSpan={2}>
                                                        <div className="gp-rq-foto-grid">
                                                            {t.foto.map((f, i) => (
                                                                <div key={f.id} className="gp-rq-foto on">
                                                                    <button type="button" className="gp-rq-foto-apri" onClick={() => onZoom(t.codice, i)} title="Ingrandisci">
                                                                        {fotoKeys[f.id]
                                                                            ? <Miniatura src={fotoImgUrl(fotoKeys[f.id], 300)} alt={`foto ${i + 1}`} />
                                                                            : <span className="gp-rq-foto-ko">non disponibile</span>}
                                                                    </button>
                                                                    <button className="gp-rq-foto-rimuovi" onClick={() => onRimuovi(f, t)} disabled={!!rimuovendo}
                                                                        title="Rimuovi dal report (non dall'archivio)" aria-label={`Rimuovi foto ${i + 1} dal report`}>
                                                                        {rimuovendo === f.id ? "…" : "✕"}
                                                                    </button>
                                                                    <span className="gp-rq-foto-data">{f.scattataIl ? new Date(f.scattataIl).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : formatData(f.data)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

/** Una riga dell'elenco tratte, col riquadro foto apribile sotto. */
const RigaTratta = ({ t, inclusa, fotoSel, fatti, aperte, onToggle, onToggleFoto, onApriFoto, onZoom }) => (
    <>
        <tr className={inclusa ? "gp-rq-inclusa" : ""}>
            <td>
                <input type="checkbox" checked={inclusa} onChange={onToggle} aria-label={`Includi ${t.strada} ${t.tratto}`} />
            </td>
            <td onClick={onToggle} className="gp-row-click">
                <div className="strong">{t.strada}</div>
                <div className="sub">{t.tratto || "—"} · <span className="codice">{t.codice}</span>{t.lunghezzaMetri ? ` · ${nf(t.lunghezzaMetri)} m` : ""}{t.fascia ? ` · ${t.fascia}` : ""}</div>
            </td>
            <td><span className={`badge ${BADGE_STATO[t.stato]}`}>{ETICHETTA_STATO[t.stato]}</span></td>
            <td className="gp-rep-nowrap">
                <span title={t.passaggi.map((p) => `${formatData(p.data)} ${p.esito === "fatta" ? "✓" : p.esito === "non_fatta" ? "✗" : "…"}`).join("\n")}>
                    {nf(fatti)} / {nf(t.passaggi.length)}
                </span>
            </td>
            <td className="gp-rep-nowrap">
                {t.foto.length === 0 ? <span className="muted">—</span> : (
                    <button className="gp-rq-foto-btn" onClick={onApriFoto} disabled={!inclusa} title={inclusa ? "Scegli le foto" : "Includi la tratta per scegliere le foto"}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                        {inclusa ? `${nf(fotoSel.size)} / ${nf(t.foto.length)}` : nf(t.foto.length)}
                        <span className={`chev ${aperte ? "open" : ""}`}>▾</span>
                    </button>
                )}
            </td>
            <td>
                {!t.geometria
                    ? <span className="badge badge-neutral" title="Nessuna geometria: non compare sulla mappa">no</span>
                    : t.geometriaQualita === "via-intera"
                        ? <span className="badge badge-warning" title="Il pezzo esatto non e' delimitabile: sulla mappa finisce l'intera via, piu' lunga del tratto spazzato">via intera</span>
                        : (
                            <span className={`badge ${t.geometriaAttendibile === false ? "badge-warning" : "badge-success"}`}
                                title={[
                                    t.geometriaEstremi ? `Da ${t.geometriaEstremi.da} a ${t.geometriaEstremi.a}` : "Geometria del solo tratto",
                                    t.geometriaLunghezza ? `${nf(t.geometriaLunghezza)} m ritagliati contro ${nf(t.lunghezzaMetri)} dichiarati` : null,
                                    t.geometriaAttendibile === false ? "Lunghezze distanti: conviene controllare il disegno" : null,
                                ].filter(Boolean).join(" · ")}>
                                {t.geometriaAttendibile === false ? "tratto ?" : "tratto"}
                            </span>
                        )}
            </td>
        </tr>
        {aperte && inclusa && t.foto.length > 0 && (
            <tr className="gp-rq-foto-riga">
                <td colSpan={6}>
                    <div className="gp-rq-foto-grid">
                        {t.foto.map((f, i) => {
                            const on = fotoSel.has(f.key);
                            return (
                                <div key={f.key} className={`gp-rq-foto ${on ? "on" : "off"}`}>
                                    <input type="checkbox" checked={on} onChange={() => onToggleFoto(f.key)}
                                        title={on ? "Escludi dal report" : "Includi nel report"} aria-label={`${on ? "Escludi" : "Includi"} foto ${i + 1}`} />
                                    <button type="button" className="gp-rq-foto-apri" onClick={() => onZoom(i)} title="Ingrandisci per scegliere">
                                        <Miniatura src={fotoImgUrl(f.key, 300)} alt={`foto ${i + 1}`} />
                                    </button>
                                    <span className="gp-rq-foto-data">{f.scattataIl ? new Date(f.scattataIl).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : formatData(f.data)}</span>
                                </div>
                            );
                        })}
                    </div>
                </td>
            </tr>
        )}
    </>
);

export default EditorReportQuartiere;
